import { useEffect } from "react";
import * as THREE from "three";
import { getSavedLightWorldPose } from "../../utils/savedLightWorldPose";
import { EMPTY_OBJECT, SCENE_LIGHT_OBJECTS_GROUP_NAME } from "../scene/sceneConstants";
import { disposeSceneObjectTree } from "../scene/sceneDispose";

const toColor = (value, fallback = [0.7, 0.7, 0.7]) => {
  const vec = Array.isArray(value) && value.length >= 3 ? value : fallback;
  return new THREE.Color(
    Math.max(0, Number(vec[0]) || 0),
    Math.max(0, Number(vec[1]) || 0),
    Math.max(0, Number(vec[2]) || 0)
  );
};

const configureShadow = (light, index) => {
  if (!light?.shadow) return;
  light.castShadow = true;
  const size = index === 0 ? 2048 : 1024;
  light.shadow.mapSize.width = size;
  light.shadow.mapSize.height = size;
  if (light.shadow.camera) {
    light.shadow.camera.near = 0.5;
    light.shadow.camera.far = 50;
    light.shadow.camera.left = -10;
    light.shadow.camera.right = 10;
    light.shadow.camera.top = 10;
    light.shadow.camera.bottom = -10;
  }
  light.shadow.bias = -0.0002;
  light.shadow.normalBias = 0.3;
};

const createLightObject = (lightDef, pose, index) => {
  const type = String(lightDef?.type || "spot").toLowerCase();
  const color = toColor(lightDef?.diffuse);
  const intensity = Math.max(0, Number(lightDef?.intensity ?? 1) || 0);
  let light;

  if (type === "directional") {
    light = new THREE.DirectionalLight(color, intensity);
  } else if (type === "point") {
    light = new THREE.PointLight(color, intensity, Math.max(0, Number(lightDef?.range) || 0));
  } else {
    light = new THREE.SpotLight(
      color,
      intensity,
      Math.max(0, Number(lightDef?.range) || 0),
      THREE.MathUtils.degToRad(Math.max(0, Math.min(90, Number(lightDef?.cutoff) || 45))),
      Math.max(0, Number(lightDef?.exponent) || 0) / 100,
      1
    );
  }

  light.name = `sceneLight:${lightDef?.uuid || lightDef?.name || index}`;
  light.userData = {
    isSceneLightObject: true,
    sceneLightUuid: lightDef?.uuid || null,
  };
  light.position.copy(pose.position);
  if (lightDef?.castshadow !== false) configureShadow(light, index);

  if (type === "directional" || type === "spot") {
    const target = new THREE.Object3D();
    target.name = `${light.name}:target`;
    target.position.copy(pose.position).add(pose.direction.clone().multiplyScalar(10));
    light.target = target;
    const group = new THREE.Group();
    group.name = `${light.name}:group`;
    group.userData = { isSceneLightObjectGroup: true, sceneLightUuid: lightDef?.uuid || null };
    group.add(light);
    group.add(target);
    return group;
  }

  return light;
};

export function useSceneLightObjects({
  allSites,
  modelGroupRef,
  safeBodies,
  savedLights,
  sceneObjectsRef,
  sceneRef,
  structureChangeTick,
}) {
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return undefined;

    const removeLights = () => {
      const existing = scene.getObjectByName(SCENE_LIGHT_OBJECTS_GROUP_NAME);
      if (!existing) return;
      existing.parent?.remove(existing);
      disposeSceneObjectTree(existing);
    };

    removeLights();
    const lights = Object.values(savedLights || EMPTY_OBJECT).filter(
      (lightDef) => lightDef && lightDef.active !== false
    );
    if (lights.length === 0) return undefined;

    const root = new THREE.Group();
    root.name = SCENE_LIGHT_OBJECTS_GROUP_NAME;
    root.userData = { isSceneLightObjectsRoot: true };
    scene.add(root);

    lights.forEach((lightDef, index) => {
      const pose = getSavedLightWorldPose(
        lightDef,
        sceneObjectsRef.current,
        safeBodies,
        allSites || EMPTY_OBJECT,
        scene.getObjectByName("modelGroup") || modelGroupRef.current || null
      );
      root.add(createLightObject(lightDef, pose, index));
    });

    return removeLights;
  }, [savedLights, safeBodies, allSites, structureChangeTick, sceneRef, sceneObjectsRef, modelGroupRef]);
}
